import {
  createContext,
  useState,
  useEffect,
  ReactNode,
  FC,
  useCallback,
} from "react";
import { memberType } from "../types/member.ts";
import { orderType } from "../types/order.ts";
import { couponType } from "../types/coupon.ts";
import * as Sentry from "@sentry/react";
import { setGAUserProperties } from "../utils/analytics.ts";

interface MemberContextType {
  memberData: memberType | null;
  platform: string | null;
}

const defaultMemberContext: MemberContextType = {
  memberData: null,
  platform: null,
};

export const MemberContext =
  createContext<MemberContextType>(defaultMemberContext);

interface MemberProviderProps {
  children: ReactNode;
}

const MemberProvider: FC<MemberProviderProps> = ({ children }) => {
  const [memberData, setMemberData] = useState<memberType | null>(null);

  const [token, setToken] = useState<string | null>(null);
  const [platform, setPlatform] = useState<string | null>(null);
  const [application, setApplication] = useState<string | null>(null);

  const convertToMemberData = (data: memberType): memberType => {
    const hide_tabs = [...(data.hide_tabs || [])];
    const is_enabled_order = data.is_enabled_order ?? true;

    // If order is disabled, add /order to hide_tabs if not already present
    if (!is_enabled_order && !hide_tabs.includes('/order')) {
      hide_tabs.push('/order');
    }

    return {
      ...data,
      hide_tabs,
      is_enabled_order,
      is_enabled_barcode: data.is_enabled_barcode || false,
      is_enabled_member_benefits: data.is_enabled_member_benefits || false,
      is_enabled_faq: data.is_enabled_faq || false,
      is_enabled_annual_consumption: data.is_enabled_annual_consumption ?? true,
      show_member_level: data.show_member_level ?? true,
      enable_member_level_name: data.enable_member_level_name ?? false,
      member_level_name_mapping: data.member_level_name_mapping ?? {},
      enable_custom_credits_name: data.enable_custom_credits_name ?? false,
    };
  };

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const encodedToken = urlParams.get("token");
    const applicationIdParam = urlParams.get("application_id");
    const platformParam = urlParams.get("platform");
    if (encodedToken && applicationIdParam && platformParam) {
      localStorage.clear();
      const decodedToken = atob(encodedToken);

      localStorage.setItem("jwt_token", decodedToken);
      localStorage.setItem("platform", platformParam);
      localStorage.setItem("application_id", applicationIdParam);

      urlParams.delete("token");
      window.history.replaceState({}, "", window.location.pathname);
    }
    const token = localStorage.getItem("jwt_token");
    const platform = localStorage.getItem("platform");
    const applicationId = localStorage.getItem("application_id");

    if (!token || !applicationId || !platform) {
      const eventId = Sentry.captureException(new Error("Missing required initialization parameters"));
      window.location.replace(`/error?eventId=${eventId}`);
    }
    setToken(token);
    setPlatform(platform);
    setApplication(applicationId);
  }, []);

  const fetchData = useCallback(async () => {
    if (token && application && platform) {
      const url = `${import.meta.env.VITE_API_HOST}/api/v1/platform/${platform}/applications/${application}/card`;

      try {
        const response = await fetch(
          url,
          {
            method: "GET",
            headers: { Authorization: `Bearer ${token}` },
          },
        );

        if (response.status !== 200) {
          throw new Error(`API request failed with status ${response.status}`);
        }

        const data = await response.json();
        data.start_date = data.start_date ? new Date(data.start_date) : null;
        data.end_date = data.end_date ? new Date(data.end_date) : null;
        // hide membership end date if it is very far in the future
        if (data.end_date && data.end_date.getFullYear() >= 9999) {
          data.end_date = null;
        }
        data.card_orders?.forEach((order: orderType) => {
          order.order_date = new Date(order.order_date);
        });
        data.card_coupons?.forEach((coupon: couponType) => {
          coupon.start_date = coupon.start_date
            ? new Date(coupon.start_date)
            : null;
          coupon.end_date = coupon.end_date ? new Date(coupon.end_date) : null;
        });

        const processedData = convertToMemberData(data);
        setMemberData(processedData);
        setGAUserProperties(processedData);
        document.title = data.brand_name;
      } catch (error) {
        const eventId = Sentry.captureException(error);
        console.error("Error fetching or parsing member data:", error);
        window.location.replace(`/error?eventId=${eventId}`);
      }
    }
  }, [application, platform, token]);

  useEffect(() => {
    if (memberData) {
      return;
    }
    fetchData();
  }, [fetchData, memberData]);

  return (
    <MemberContext.Provider value={{ memberData, platform }}>
      {children}
    </MemberContext.Provider>
  );
};

export default MemberProvider;
